/**
 * The knife stroke, with snapping (#1013).
 *
 * ONE implementation for both knives — construction's freehand cut (#193) and
 * the leather Slice method (#1001). They are the same gesture with the same snap
 * rules on different object families, and #990 records what a second copy costs.
 *
 * Before this, both accumulated raw pointer samples straight from the cursor, so
 * a cut couldn't be aimed: it couldn't start on a corner, run along a grid line,
 * or be made straight. That's out of step with every other drawing gesture, and
 * it bites hardest here, because a cut is destructive and its result becomes two
 * independent objects.
 *
 * PRECEDENCE, matching the draw sub-mode (`drawSubModeController`) exactly:
 *
 *   1. **A vertex under the cursor wins outright.** Landing on an existing
 *      anchor is more specific than rounding to an axis. When it fires the angle
 *      hint is cleared rather than left showing — the cut is no longer at a
 *      common angle and a chip claiming otherwise would be lying. The cut stays
 *      STRAIGHT if the angle modifier is held: "straight, from here to that
 *      corner" is the useful reading of both together.
 *   2. **The angle modifier makes the cut straight**, replacing the freehand
 *      trail with a single segment at the nearest common drafting angle. With
 *      grid snap also on, the endpoint slides along that locked ray to the
 *      nearest grid crossing (#725), so the direction stays clean AND the cut
 *      lands on the grid.
 *   3. **Line, then grid**, the ordinary low-priority fallbacks.
 *
 * INTERMEDIATE SAMPLES OF A FREEHAND DRAG STAY RAW. Only the two endpoints are
 * snapped. Snapping every sample would stair-step a curve the user is
 * deliberately drawing by hand — the same call the draw sub-mode makes when it
 * skips grid snap for its `freehand` kind.
 */
import type { EditorContext } from "./EditorContext";
import {
  gatherDrawSnapTargets,
  resolveDrawSnap,
  type DrawSnapTargets,
} from "./drawSnap";
import {
  angleModifierHeld,
  isAngleSnapActive,
  isGridSnapActive,
  snapAlongRayToGrid,
  snapToCommonAngle,
  type Point2,
} from "./dragSnap";
import { setAngleHint } from "../snapAngleIndicator";

/** The modifier flags the stroke reads. A `PointerSample` satisfies this. */
export interface KnifeModifiers {
  shiftKey?: boolean;
  ctrlKey?: boolean;
  metaKey?: boolean;
}

function mods(m: KnifeModifiers): {
  shiftKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
} {
  return {
    shiftKey: !!m.shiftKey,
    ctrlKey: !!m.ctrlKey,
    metaKey: !!m.metaKey,
  };
}

/**
 * Accumulates a knife drag and resolves it to the stroke that should be
 * previewed and cut with. Owned by whichever controller owns the drag; call
 * {@link begin} on press, {@link extend} on every move, {@link replayModifiers}
 * when a modifier changes mid-drag, and {@link end} on release or cancel.
 */
export class KnifeStroke {
  /** Snap targets, gathered once per gesture — scene geometry doesn't change
   *  while a cut is in progress. */
  private targets: DrawSnapTargets | null = null;
  /** Raw cursor samples, first to last. The trail between the endpoints is used
   *  verbatim; only `[0]` and the last are replaced by their snapped versions. */
  private rawTrail: Point2[] = [];
  /** The snapped press point. */
  private startPt: Point2 | null = null;
  private lastRaw: Point2 | null = null;
  /**
   * Modifier state, tracked from its TWO sources separately and OR-ed.
   *
   * A pointer event carries the modifiers held at that moment, and a key event
   * reports a change on its own. Folding both into one field let whichever
   * arrived LAST win — so pressing the angle key mid-drag was undone by the very
   * next pointer move, and the behaviour depended on event ordering. It only
   * looked fine because browsers happen to interleave them differently: the same
   * cut came out straight under Chromium and freehand under WebKit.
   *
   * Either source reporting the key as down means it is down; a release is
   * reported by both, so nothing sticks.
   */
  private pointerMods = { shiftKey: false, ctrlKey: false, metaKey: false };
  private keyMods = { shiftKey: false, ctrlKey: false, metaKey: false };
  private resolved: Point2[] = [];
  private straight = false;

  /** Begin a cut at the press point, snapping it. */
  begin(ctx: EditorContext, p: Point2, m: KnifeModifiers): void {
    this.targets = gatherDrawSnapTargets(
      ctx.history.state,
      ctx.scene.getPaperScope(),
      ctx.scene.getPartLayoutView(),
    );
    const snapped = resolveDrawSnap(ctx, p, this.targets);
    this.startPt = snapped.point;
    ctx.scene.setDrawSnapPoint(snapped.hit?.pos ?? null);
    this.rawTrail = [{ x: p.x, y: p.y }];
    this.lastRaw = { x: p.x, y: p.y };
    this.pointerMods = mods(m);
    this.keyMods = { shiftKey: false, ctrlKey: false, metaKey: false };
    this.resolved = [this.startPt];
    this.straight = false;
  }

  /** Advance the cut to a new cursor position. */
  extend(ctx: EditorContext, p: Point2, m: KnifeModifiers): void {
    if (!this.startPt) return;
    this.rawTrail.push({ x: p.x, y: p.y });
    this.lastRaw = { x: p.x, y: p.y };
    this.pointerMods = mods(m);
    this.recompute(ctx);
  }

  /** Re-resolve at the current cursor with new modifier state, so pressing or
   *  releasing the angle key switches between a straight and a freehand cut
   *  immediately rather than on the next pointer move. */
  replayModifiers(ctx: EditorContext, m: KnifeModifiers): void {
    if (!this.startPt || !this.lastRaw) return;
    this.keyMods = mods(m);
    this.recompute(ctx);
  }

  /** The stroke to preview and cut with, in project mm. */
  points(): Point2[] {
    return this.resolved;
  }

  /** True when the angle modifier turned this into a straight cut. */
  isStraight(): boolean {
    return this.straight;
  }

  /** How far the cursor has travelled from the press point, for slop checks.
   *  Measured on the RAW positions: whether the gesture counts as a drag is
   *  about what the hand did, not where a snap decided to put the result. */
  rawTravelMm(): number {
    const a = this.rawTrail[0];
    const b = this.lastRaw;
    if (!a || !b) return 0;
    return Math.hypot(b.x - a.x, b.y - a.y);
  }

  /** Drop the stroke and its on-canvas feedback. */
  end(ctx: EditorContext): void {
    ctx.scene.setDrawSnapPoint(null);
    setAngleHint(null);
    this.targets = null;
    this.rawTrail = [];
    this.startPt = null;
    this.lastRaw = null;
    this.resolved = [];
    this.straight = false;
  }

  // -------------------------------------------------------------------

  private recompute(ctx: EditorContext): void {
    const start = this.startPt;
    const raw = this.lastRaw;
    if (!start || !raw) return;
    const targets = this.targets ?? {
      vertices: [],
      edges: [],
      intersections: [],
    };
    const snapped = resolveDrawSnap(ctx, raw, targets);
    this.straight = isAngleSnapActive(
      ctx.settings,
      angleModifierHeld({
        shiftKey: this.pointerMods.shiftKey || this.keyMods.shiftKey,
        ctrlKey: this.pointerMods.ctrlKey || this.keyMods.ctrlKey,
        metaKey: this.pointerMods.metaKey || this.keyMods.metaKey,
      }),
    );

    let end: Point2;
    if (snapped.hit?.kind === "vertex") {
      // Tier 1 — an anchor under the cursor. Beats the angle, and takes the
      // hint down with it so the chip can't claim an angle the cut isn't at.
      end = snapped.point;
      ctx.scene.setDrawSnapPoint(snapped.hit.pos);
      setAngleHint(null);
    } else if (this.straight) {
      // Tier 2 — straight cut at the nearest common angle.
      const angled = snapToCommonAngle(start, raw);
      end = { x: angled.x, y: angled.y };
      setAngleHint(angled.angleRad);
      let onGrid: Point2 | null = null;
      if (isGridSnapActive(ctx.settings)) {
        const grid = ctx.scene.getMatGridInfo();
        if (grid) onGrid = snapAlongRayToGrid(start, end, grid);
      }
      if (onGrid) end = onGrid;
      // The dot marks a grid crossing when one caught; otherwise the endpoint
      // is wherever the angle put it and there is nothing to point at.
      ctx.scene.setDrawSnapPoint(onGrid);
    } else {
      // Tier 3 — line, then grid, as `resolveDrawSnap` already ordered them.
      end = snapped.point;
      ctx.scene.setDrawSnapPoint(snapped.hit?.pos ?? null);
      setAngleHint(null);
    }

    this.resolved = this.straight
      ? [start, end]
      : [start, ...this.rawTrail.slice(1, -1), end];
  }
}
